import {useCallback, useEffect, useState} from 'react';
import {useCustomEvent} from './useCustomEvent';

// Keys migrating to use this hook may be 'raw' strings, rather than JSON.
function stringSafeParse<T>(savedState: string): T | null {
	try {
		return JSON.parse(savedState);
	} catch {
		return JSON.parse(`"${savedState}"`);
	}
}

type InitFn<T> = (value: unknown) => T;
function isInitialiserFn<T>(init: T | InitFn<T> | null): init is InitFn<T> {
	return typeof init === 'function';
}

type EmptyState<T> = [T | null, React.Dispatch<React.SetStateAction<T | null>>];
type InitialisedState<T> = [T, React.Dispatch<React.SetStateAction<T>>];

export function useStoredState<T>(key: string): EmptyState<T>;
export function useStoredState<T>(key: string, initialValue: T): InitialisedState<T>;
export function useStoredState<T>(key: string, initialiser: InitFn<T>): InitialisedState<T>;

export function useStoredState<T>(key: string, init?: T | InitFn<T>): EmptyState<T> | InitialisedState<T> {
	const [state, setState] = useState<T | null>(() => {
		const savedState = localStorage.getItem(key);

		if (init === undefined) {
			return savedState ? stringSafeParse<T>(savedState) : null;
		} else if (!isInitialiserFn(init)) {
			return savedState ? stringSafeParse<T>(savedState) : init;
		} else {
			return init(savedState && stringSafeParse(savedState));
		}
	});

	const handleStateUpdate = useCallback((value: T | null) => setState(value), [key, setState]);

	const dispatchEvent = useCustomEvent<T | null>(`storedStateUpdated:${key}`, handleStateUpdate);

	useEffect(() => {
		dispatchEvent(state);
		if (state === null) {
			localStorage.removeItem(key);
		} else {
			localStorage.setItem(key, JSON.stringify(state));
		}
	}, [state]);

	return [state, setState];
}
